/**
 * Card Variant Filter
 * 
 * Filters product card variants based on delivery preference and metafield availability data.
 * Works with card-product.liquid's embedded card-variant-availability JSON.
 * 
 * Responsibilities:
 * - Filter variant prices on the card face (show/hide based on delivery preference)
 * - Filter variant radios in the ATC panel (show/hide/disable based on delivery preference)
 * - Hide quick-add button when product is completely unavailable
 * - Remove the whole slide in carousels marked data-hide-unavailable
 * - Dynamically toggle boozeclub__active class based on visible variants
 * - Show loading spinner while processing, then reveal filtered content
 *
 * Visibility Logic (matches store-variant-filter.js):
 * - Delivery mode: Show variants where delivery_preference_availability === "Online"
 * - Store mode: Show only variants where delivery_preference_availability === "Store"
 *   and store_id equals the selected store's metaobject id
 * - Store mode with no resolvable store id: apply the delivery rule, mirroring
 *   buildStoreFilterParams in collection-store-filter.js (CU-86d45dttb)
 *
 * Availability Logic (among visible variants):
 * - Delivery mode: Disabled if variant.available === false
 * - Store mode: Disabled if store_is_available === false or variant.available === false
 */

(function() {
  'use strict';

  // WeakSet to track which cards have been processed for the current preference
  var processedCards = new WeakSet();

  // Debounce timer for MutationObserver
  var mutationDebounceTimer = null;

  // ========================================================================
  // Core Filtering Logic (mirrors store-variant-filter.js)
  // ========================================================================

  const DELIVERY_PREFERENCE_KEY = 'deliveryPreference';
  const OLD_STORAGE_KEY = 'selectedStore';

  /**
   * Read delivery preference from localStorage (mirrors delivery-selector.js logic).
   * Used when DeliverySelector module is not yet loaded (script load order).
   * @returns {Object} Preference object { deliveryType, deliveryLocation, storeData }
   */
  function getDeliveryPreferenceFromStorage() {
    try {
      var stored = localStorage.getItem(DELIVERY_PREFERENCE_KEY);
      if (stored) {
        var preference = JSON.parse(stored);
        if (preference &&
            (preference.deliveryType === 'DELIVERY' || preference.deliveryType === 'STORE') &&
            (preference.deliveryLocation === null || typeof preference.deliveryLocation === 'string')) {
          if (!preference.hasOwnProperty('storeData')) {
            preference.storeData = null;
          }
          return preference;
        }
      }
      // Migrate old format
      var oldValue = localStorage.getItem(OLD_STORAGE_KEY);
      if (oldValue === 'Online') {
        return { deliveryType: 'DELIVERY', deliveryLocation: null, storeData: null };
      }
      if (typeof oldValue === 'string' && oldValue.trim() !== '' && oldValue !== 'Online') {
        return { deliveryType: 'STORE', deliveryLocation: oldValue.trim(), storeData: null };
      }
    } catch (e) {
      /* ignore */
    }
    return { deliveryType: 'DELIVERY', deliveryLocation: null, storeData: null };
  }

  /**
   * Get delivery preference from DeliverySelector module or localStorage fallback
   * @returns {Object} Preference object { deliveryType, deliveryLocation, storeData }
   */
  function getDeliveryPreference() {
    if (window.DeliverySelector && typeof window.DeliverySelector.getDeliveryPreference === 'function') {
      return window.DeliverySelector.getDeliveryPreference();
    }
    return getDeliveryPreferenceFromStorage();
  }

  /**
   * Determine if a variant should be visible based on delivery preference
   *
   * Store mode matches on the store metaobject id, the same identity
   * collection-store-filter.js sends to Shopify as
   * filter.v.m.stores.store_availability. Matching on store_name instead let a
   * product pass the collection facet filter and still render as unavailable on
   * its own card, which is the CU-86d45dttb defect.
   *
   * A STORE preference with no resolvable store id falls through to the online
   * rule, mirroring buildStoreFilterParams in collection-store-filter.js. Both
   * layers must degrade identically or they disagree again — a preference
   * migrated from the old selectedStore key carries a store name and no id.
   *
   * @param {Object} variantData - Variant availability data
   * @param {Object} deliveryPreference - Current delivery preference
   * @returns {boolean} True if variant should be shown
   */
  function isVariantVisible(variantData, deliveryPreference) {
    if (!variantData) return false;

    var deliveryType = deliveryPreference.deliveryType;
    var selectedStoreId = deliveryPreference.storeData && deliveryPreference.storeData.id;

    if (deliveryType === 'STORE' && selectedStoreId) {
      return variantData.delivery_preference_availability === "Store" &&
        String(variantData.store_id) === String(selectedStoreId);
    }

    if (deliveryType === 'DELIVERY' || deliveryType === 'STORE') {
      return variantData.delivery_preference_availability === "Online";
    }

    return false;
  }

  /**
   * Determine if a visible variant is disabled (sold out / unavailable)
   * @param {Object} variantData - Variant availability data
   * @param {Object} deliveryPreference - Current delivery preference
   * @returns {boolean} True if variant should be disabled
   */
  function isVisibleVariantDisabled(variantData, deliveryPreference) {
    if (!variantData) return true;

    var deliveryType = deliveryPreference.deliveryType;
    var selectedStoreId = deliveryPreference.storeData && deliveryPreference.storeData.id;

    // Always disabled if Shopify native stock check fails
    if (variantData.available === false) {
      return true;
    }

    // Gated on a resolvable store id for the same reason isVariantVisible is:
    // without one, store mode shows online variants, and an online variant must
    // not then be disabled for failing a pickup check that no longer applies.
    if (deliveryType === 'STORE' && selectedStoreId) {
      if (variantData.delivery_preference_availability !== "Store") {
        return true;
      }
      if (variantData.store_is_available === false) {
        return true;
      }
    }

    return false;
  }

  /**
   * Summarise one product's variants for a delivery preference: the ids of the
   * variants shown for it, the ids of those that can be bought, and whether any
   * shown variant carries a club price. processCard uses it for every card, and
   * Recently Viewed (assets/recently-viewed.js) uses it through
   * window.CardVariantFilter, so both judge a product the same way. CU-86d46vcvq.
   * @param {Object|null} availabilityData - variant availability JSON, keyed by variant id
   * @param {Object|null} deliveryPreference - { deliveryType, deliveryLocation, storeData }
   * @returns {Object} { visibleIds: Array, availableIds: Array, visibleHasClubPrice: boolean }
   */
  function summariseAvailability(availabilityData, deliveryPreference) {
    var summary = { visibleIds: [], availableIds: [], visibleHasClubPrice: false };
    if (!availabilityData || !deliveryPreference) return summary;

    Object.keys(availabilityData).forEach(function(variantId) {
      var data = availabilityData[variantId];
      if (!isVariantVisible(data, deliveryPreference)) return;

      summary.visibleIds.push(variantId);
      if (data.has_club_price === true) summary.visibleHasClubPrice = true;
      if (!isVisibleVariantDisabled(data, deliveryPreference)) summary.availableIds.push(variantId);
    });

    return summary;
  }

  /**
   * The Sale badge shows iff some variant relevant to the current fulfilment
   * preference is BOTH purchasable and on sale.
   *
   * Existential, not first-match: a shopper at one store can have several relevant
   * variants (the CU-86d3z45na test product gives a Marrickville shopper two — Each
   * at $75.99 and Pack (2) at $140.99), and any one of them being on sale must show
   * the badge.
   *
   * Requiring "not disabled" reproduces the server condition's `and product.available`
   * clause and stops a sold-out pickup variant from driving a Sale badge.
   *
   * PURE BY CONSTRUCTION. Its only free identifiers are isVariantVisible,
   * isVisibleVariantDisabled and the built-in Object — no document, no window, no
   * module-scope state, no timers, no template literals, no string literals. That is
   * what lets test/sale-gate-predicates.test.js extract this body and instantiate it
   * with new Function, injecting this file's own two predicates as arguments. The DOM
   * write lives in the caller and must stay there.
   *
   * This body is BYTE-IDENTICAL in card-variant-filter.js, store-variant-filter.js and
   * predictive-search.js, and a test asserts it. Change one, change all three.
   *
   * @param {Object|null} availabilityData - variant availability JSON, keyed by variant id
   * @param {Object|null} deliveryPreference - { deliveryType, deliveryLocation, storeData }
   * @returns {boolean}
   */
  function isSaleBadgeVisible(availabilityData, deliveryPreference) {
    if (!availabilityData) return false;
    if (!deliveryPreference) return false;
    var variantIds = Object.keys(availabilityData);
    for (var i = 0; i < variantIds.length; i++) {
      var data = availabilityData[variantIds[i]];
      if (!data) continue;
      if (data.on_sale !== true) continue;
      if (!isVariantVisible(data, deliveryPreference)) continue;
      if (isVisibleVariantDisabled(data, deliveryPreference)) continue;
      return true;
    }
    return false;
  }

  /**
   * Write a terminal data-sale-gate state from the element's own fallback attribute.
   *
   * Called from every path that cannot compute the verdict, so no hook is ever left in
   * "pending" — which base.css hides under .js, meaning the badge would never paint.
   *
   * Contract on a missing or malformed data-sale-gate-fallback: NO-OP. The snippet emits
   * the fallback under exactly the condition that makes the state "pending" (invariant
   * I-2), so an element without one already carries a terminal on/off state and
   * overwriting it would destroy a correct server verdict.
   *
   * This body is BYTE-IDENTICAL in card-variant-filter.js, store-variant-filter.js and
   * predictive-search.js, and a test asserts it. Change one, change all three.
   *
   * @param {Element} el - an element carrying [data-sale-label]
   * @returns {void}
   */
  function resolveSaleGateFallback(el) {
    var fallback = el.getAttribute('data-sale-gate-fallback');
    if (fallback !== 'on' && fallback !== 'off') return;
    el.setAttribute('data-sale-gate', fallback);
  }

  // ========================================================================
  // Data Access
  // ========================================================================

  /**
   * Read variant availability data from a card element's embedded JSON
   * @param {HTMLElement} cardElement - The <card-product> element
   * @returns {Object|null} Variant availability data keyed by variant ID string
   */
  function getCardAvailabilityData(cardElement) {
    var scriptTag = cardElement.querySelector('.card-variant-availability');
    if (!scriptTag) {
      return null;
    }

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error('[CardVariantFilter] Error parsing card-variant-availability JSON:', e);
      return null;
    }
  }

  // ========================================================================
  // Per-Card Processing
  // ========================================================================

  /**
   * Process a single card: filter prices, radios, and set unavailable state
   * @param {HTMLElement} cardElement - The <card-product> element
   */
  function processCard(cardElement) {
    // Card-owned Sale hooks only. A hook carrying [data-sale-gate-scope] belongs to a
    // section, and quick-add.js:26-29 can inject one into this very card — it copies
    // section[id^="MainProduct-"] into #QuickAddInfo-{id}, which sits inside
    // <card-product>. store-variant-filter.js owns those, so the card filter must never
    // write them from card data. CU-86d3z45na.
    var saleHooks = cardElement.querySelectorAll('[data-sale-label]:not([data-sale-gate-scope])');

    var availabilityData = getCardAvailabilityData(cardElement);
    if (!availabilityData) {
      // No availability data - resolve every hook to its server-side fallback so none is
      // left pending (which CSS hides), then remove loading state and leave card as-is
      for (var h = 0; h < saleHooks.length; h++) {
        resolveSaleGateFallback(saleHooks[h]);
      }
      cardElement.classList.remove('card--cvf-loading');
      return;
    }

    var deliveryPreference = getDeliveryPreference();

    // -- Sale-label fulfilment gate (CU-86d3z45na) --
    // Inside the card--cvf-loading gate (removed at the end of this function), so the
    // badge never paints in a stale state. Writes both .card__badge containers: the live
    // one and the dormant quick-add one.
    var saleVerdict = isSaleBadgeVisible(availabilityData, deliveryPreference) ? 'on' : 'off';
    for (var s = 0; s < saleHooks.length; s++) {
      saleHooks[s].setAttribute('data-sale-gate', saleVerdict);
    }

    var summary = summariseAvailability(availabilityData, deliveryPreference);
    var visibleVariantIds = summary.visibleIds;
    var availableVariantIds = summary.availableIds;
    var visibleHasClubPrice = summary.visibleHasClubPrice;

    // -- Filter variant prices on card face --
    filterVariantPrices(cardElement, visibleVariantIds, availableVariantIds);

    // -- Filter ATC panel radios --
    filterATCPanelVariants(cardElement, visibleVariantIds, availableVariantIds, availabilityData, deliveryPreference);

    // -- BoozeClub class re-evaluation --
    if (visibleHasClubPrice) {
      if (!cardElement.classList.contains('boozeclub__active')) {
        cardElement.classList.add('boozeclub__active');
      }
    } else {
      cardElement.classList.remove('boozeclub__active');
    }

    // -- Product-level unavailability --
    var hasVisibleVariants = visibleVariantIds.length > 0;
    var hasAvailableVariants = availableVariantIds.length > 0;

    if (!hasVisibleVariants) {
      // No variants match delivery preference at all — hide prices + quick-add, show message
      cardElement.classList.add('card--store-unavailable');
      cardElement.classList.remove('card--all-sold-out');
      updateUnavailableMessage(cardElement, deliveryPreference);
    } else if (!hasAvailableVariants) {
      // Variants match but all are out of stock — show prices, show disabled button
      cardElement.classList.remove('card--store-unavailable');
      cardElement.classList.add('card--all-sold-out');
    } else {
      cardElement.classList.remove('card--store-unavailable');
      cardElement.classList.remove('card--all-sold-out');
    }

    // -- Update quick ATC button state to reflect filtered availability --
    updateQuickATCButton(cardElement, hasVisibleVariants, hasAvailableVariants);

    // -- Remove loading state to reveal filtered content --
    cardElement.classList.remove('card--cvf-loading');

    // Mark as processed
    processedCards.add(cardElement);
  }

  /**
   * Show, hide and mark the variant options on the card face.
   * A visible option whose variant is not in availableVariantIds is marked sold out:
   * the same decision filterATCPanelVariants turns into .variant-sold-out on the
   * quick-add radio. A hidden option loses every sold-out mark, so no stale state
   * survives a change of delivery method or store. The option stays a working link,
   * as a sold-out option on the product page stays clickable. CU-14ym9bv643b.
   * @param {HTMLElement} cardElement
   * @param {Array} visibleVariantIds - Variant IDs shown for the current preference
   * @param {Array} availableVariantIds - Visible variant IDs that can be bought
   */
  function filterVariantPrices(cardElement, visibleVariantIds, availableVariantIds) {
    var priceList = cardElement.querySelector('.variant-prices');
    if (!priceList) return;

    var priceItems = priceList.querySelectorAll('.variant-price[data-variant-id]');
    var visibleCount = 0;

    priceItems.forEach(function(item) {
      var variantId = item.getAttribute('data-variant-id');
      var isVisible = visibleVariantIds.indexOf(variantId) !== -1;
      var isSoldOut = isVisible && availableVariantIds.indexOf(variantId) === -1;
      var soldOutText = item.querySelector('.variant-price__sold-out');

      item.style.display = isVisible ? '' : 'none';
      if (isVisible) visibleCount++;

      if (isSoldOut) {
        item.classList.add('variant-price--sold-out');
        item.setAttribute('data-sold-out', 'true');
      } else {
        item.classList.remove('variant-price--sold-out');
        item.removeAttribute('data-sold-out');
      }

      if (soldOutText) {
        if (isSoldOut) {
          soldOutText.classList.remove('hidden');
        } else {
          soldOutText.classList.add('hidden');
        }
      }
    });

    // Update layout class based on visible count
    if (visibleCount <= 2) {
      priceList.classList.add('two-col');
    } else {
      priceList.classList.remove('two-col');
    }
  }

  /**
   * Filter variant radios in the ATC panel
   * @param {HTMLElement} cardElement
   * @param {Array} visibleVariantIds
   * @param {Array} availableVariantIds
   * @param {Object} availabilityData
   * @param {Object} deliveryPreference
   */
  function filterATCPanelVariants(cardElement, visibleVariantIds, availableVariantIds, availabilityData, deliveryPreference) {
    var radioContainers = cardElement.querySelectorAll('.radio-toggle-container[data-variant-id]');
    if (!radioContainers.length) return;

    var firstVisibleRadio = null;
    var firstAvailableRadio = null;
    var currentCheckedWasHidden = false;

    radioContainers.forEach(function(container) {
      var variantId = container.getAttribute('data-variant-id');
      var radio = container.querySelector('input[type="radio"]');
      if (!radio) return;

      var isVisible = visibleVariantIds.indexOf(variantId) !== -1;
      var isAvailable = availableVariantIds.indexOf(variantId) !== -1;

      if (isVisible) {
        // Show the variant
        container.style.display = '';
        container.removeAttribute('hidden');

        if (!firstVisibleRadio) {
          firstVisibleRadio = radio;
        }

        // Apply sold-out styling if unavailable
        if (!isAvailable) {
          container.classList.add('variant-sold-out');
          radio.dataset.soldOut = 'true';
        } else {
          container.classList.remove('variant-sold-out');
          delete radio.dataset.soldOut;

          if (!firstAvailableRadio) {
            firstAvailableRadio = radio;
          }
        }
      } else {
        // Hide the variant
        container.style.display = 'none';
        container.setAttribute('hidden', '');
        container.classList.remove('variant-sold-out');
        delete radio.dataset.soldOut;

        // Track if the currently checked radio was hidden
        if (radio.checked) {
          currentCheckedWasHidden = true;
          radio.checked = false;
        }
      }
    });

    // Auto-select first available variant if current selection was hidden or none selected
    if (currentCheckedWasHidden || !getCheckedRadio(cardElement)) {
      var radioToSelect = firstAvailableRadio || firstVisibleRadio;
      if (radioToSelect) {
        radioToSelect.checked = true;
        radioToSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Get the currently checked radio in a card's ATC panel
   * @param {HTMLElement} cardElement
   * @returns {HTMLInputElement|null}
   */
  function getCheckedRadio(cardElement) {
    return cardElement.querySelector('.radio-toggle-container:not([hidden]) input[type="radio"]:checked');
  }

  /**
   * Update the quick ATC button state to reflect filtered availability.
   * Dynamically toggles the button between "Add to cart" (enabled) and "Sold out" (disabled)
   * based on whether any purchasable variants exist for the current delivery preference.
   * @param {HTMLElement} cardElement
   * @param {boolean} hasVisibleVariants - At least one variant matches the delivery preference
   * @param {boolean} hasAvailableVariants - At least one visible variant is in stock
   */
  function updateQuickATCButton(cardElement, hasVisibleVariants, hasAvailableVariants) {
    var button = cardElement.querySelector('.card_quick-atc');
    if (!button) return;

    // Don't modify pre-order buttons — their state is fixed by Liquid
    if (button.dataset.preorder) return;

    var textSpan = button.querySelector('span');
    if (!textSpan) return;

    var addText = button.dataset.addText || 'Add to cart';
    var soldOutText = button.dataset.soldOutText || 'Sold out';

    if (!hasVisibleVariants) {
      // No matching variants — button is hidden by card--store-unavailable CSS anyway,
      // but set disabled as a safety net
      button.disabled = true;
      button.dataset.soldout = 'true';
      textSpan.textContent = soldOutText;
    } else if (!hasAvailableVariants) {
      // Matching variants but all out of stock — show disabled "Sold out" button
      button.disabled = true;
      button.dataset.soldout = 'true';
      textSpan.textContent = soldOutText;
    } else {
      // Purchasable variants exist — show enabled "Add to cart" button
      button.disabled = false;
      delete button.dataset.soldout;
      textSpan.textContent = addText;
    }
  }

  /**
   * Set the unavailable message text when product has no variants for the delivery preference.
   * @param {HTMLElement} cardElement
   * @param {Object} deliveryPreference - { deliveryType, deliveryLocation, storeData }
   */
  function updateUnavailableMessage(cardElement, deliveryPreference) {
    var textEl = cardElement.querySelector('.card-unavailable-message__text');
    if (!textEl) return;

    var message;
    if (deliveryPreference.deliveryType === 'DELIVERY') {
      message = cardElement.dataset.notAvailableDelivery ||
        (window.variantStrings && window.variantStrings.productNotAvailableDelivery) ||
        'This product is not available for delivery';
    } else {
      var storeName = (deliveryPreference.storeData && deliveryPreference.storeData.store_name) ||
        deliveryPreference.deliveryLocation;
      var template = cardElement.dataset.notAvailableStore ||
        (window.variantStrings && window.variantStrings.productNotAvailableStore) ||
        'This product is not available at {store}';
      message = template.replace('{store}', storeName || 'this store');
    }
    textEl.textContent = message;
  }

  // ========================================================================
  // Carousel Pruning (CU-86d45dttb)
  // ========================================================================

  /**
   * Carousels render their products in Liquid, so nothing filters them the way
   * collection-store-filter.js filters a collection or search grid. Until this,
   * a product with no variants at the selected store still occupied a slide and
   * showed an unavailable message. This removes the slide instead.
   *
   * Carousels opt in with data-hide-unavailable in their own markup.
   *
   * Collection and search grids are matched by ID INSTEAD OF that attribute,
   * even though their Liquid carries it too. FacetFiltersForm replaces the grid
   * markup with HTML fetched from ?section_id=, and the replacement was
   * observed coming back without the attribute — the backstop then silently
   * stopped working for that render, which is how Corona Extra survived a
   * Hawksburn search. #product-grid is Dawn's own anchor for that replacement,
   * so it is there whatever the fetch returns: a <ul> on the collection
   * template, a <div> wrapping the <ul> on search.
   */
  var HIDE_CONTAINER_SELECTOR = [
    '[data-hide-unavailable]',
    'ul#product-grid',
    '#product-grid ul.product-grid'
  ].join(', ');
  var HIDDEN_SLIDE_CLASS = 'grid__item--unavailable-hidden';
  var SINGLE_SLIDE_CLASS = 'slider--pruned-single';

  /**
   * Does this card's availability JSON carry the store_id key?
   *
   * Absent means the markup was rendered before store_id was emitted, NOT that
   * the product has no store. The two must not be confused: a present-but-null
   * store_id is a delivery-only variant and is correctly hidden in store mode,
   * whereas an absent key means every variant fails the id match and the card
   * reads as unavailable no matter which store is selected. Hiding on that is
   * how a whole search page can vanish.
   *
   * Reachable in a live session, not only in theory: FacetFiltersForm swaps the
   * grid for HTML fetched from ?section_id=, and that fetch can return markup
   * from a theme that predates the fix.
   *
   * @param {HTMLElement} cardElement
   * @returns {boolean} True when the card can be judged in store mode
   */
  function cardCarriesStoreIds(cardElement) {
    var script = cardElement.querySelector('script.card-variant-availability');
    if (!script) return false;
    try {
      var data = JSON.parse(script.textContent);
      var ids = Object.keys(data);
      for (var i = 0; i < ids.length; i++) {
        if (data[ids[i]] && Object.prototype.hasOwnProperty.call(data[ids[i]], 'store_id')) {
          return true;
        }
      }
    } catch (e) {
      /* malformed JSON is not a verdict either */
    }
    return false;
  }

  /**
   * Hide or restore one slide. Uses a class rather than an inline style so a
   * theme editor reload cannot leave an orphaned display value behind.
   * @param {HTMLElement} slide
   * @param {boolean} hidden
   */
  function setSlideHidden(slide, hidden) {
    if (hidden) {
      slide.classList.add(HIDDEN_SLIDE_CLASS);
      slide.setAttribute('aria-hidden', 'true');
    } else {
      slide.classList.remove(HIDDEN_SLIDE_CLASS);
      slide.removeAttribute('aria-hidden');
    }
  }

  /**
   * Prune every opted-in carousel to the products available at the selected
   * store, then hand the slider its new slide count.
   *
   * Always evaluates both directions. A preference change can make a hidden
   * product available again, and a one-way sweep would leave it hidden.
   */
  function pruneUnavailableFromCarousels() {
    var containers = document.querySelectorAll(HIDE_CONTAINER_SELECTOR);

    containers.forEach(function(container) {
      var slides = container.querySelectorAll('.grid__item');
      var visibleCount = 0;

      slides.forEach(function(slide) {
        var card = slide.querySelector('card-product');
        // A slide with no card is not ours to judge — leave it alone and count
        // it, so a promotional tile cannot empty a carousel on its own.
        // Card markup written before store_id existed is not ours to judge
        // either: every variant would fail the id match and the whole grid
        // would disappear. Observed live — a facet re-render fetched grid HTML
        // whose cards had no store_id and all 32 search results were hidden.
        if (!card || !cardCarriesStoreIds(card)) {
          visibleCount++;
          return;
        }
        var unavailable = card.classList.contains('card--store-unavailable');
        setSlideHidden(slide, unavailable);
        if (!unavailable) visibleCount++;
      });

      var slider = container.closest('slider-component');

      // A CAROUSEL with nothing left is a heading over an empty rail, so the
      // whole section goes. A collection or search grid must not: emptying it
      // would blank the page and take the filters, sort and pagination with it.
      // Those two keep their own empty state, so the slider test is the gate.
      var section = container.closest('.shopify-section');
      if (section && slider) {
        section.classList.toggle('section--unavailable-hidden', visibleCount === 0);
      }

      // initPages() filters on clientWidth, so it must run after the hides land
      // and it must be told to re-read: the ResizeObserver watches the slider's
      // own box, which removing children does not necessarily change.
      if (slider && typeof slider.resetPages === 'function') {
        slider.resetPages();
      }

      // initPages() returns early below two visible slides, so totalPages keeps
      // whatever it held before the prune: a carousel cut to one product read
      // "1 / 9" with both arrows live. Retire the controls and pin the counter.
      if (slider) {
        slider.classList.toggle(SINGLE_SLIDE_CLASS, visibleCount < 2);
        if (visibleCount < 2) {
          var total = slider.querySelector('.slider-counter--total');
          var current = slider.querySelector('.slider-counter--current');
          if (total) total.textContent = visibleCount;
          if (current) current.textContent = visibleCount;
        }
      }
    });
  }

  // ========================================================================
  // Batch Processing
  // ========================================================================

  /**
   * Process all card-product elements on the page
   * @param {boolean} force - If true, re-process even already-processed cards
   */
  function processAllCards(force) {
    var cards = document.querySelectorAll('card-product');
    cards.forEach(function(card) {
      if (force || !processedCards.has(card)) {
        processCard(card);
      }
    });
    pruneUnavailableFromCarousels();
  }

  /**
   * Re-add loading state to all cards and re-process them
   * Used when delivery preference changes
   */
  function reprocessAllCards() {
    // Reset processed tracking
    processedCards = new WeakSet();

    var cards = document.querySelectorAll('card-product');
    cards.forEach(function(card) {
      // Re-add loading state to show spinner and hide stale content
      card.classList.add('card--cvf-loading');
      // Remove stale unavailable/sold-out states (will be re-evaluated)
      card.classList.remove('card--store-unavailable');
      card.classList.remove('card--all-sold-out');
    });

    // Process after a brief delay to allow the loading state to render
    setTimeout(function() {
      processAllCards(true);
    }, 10);
  }

  // ========================================================================
  // Event Listeners & Initialization
  // ========================================================================

  function setupEventListeners() {
    // Listen for delivery preference changes
    document.addEventListener('deliveryPreference:changed', function() {
      reprocessAllCards();
    });
  }

  /**
   * Set up MutationObserver to detect dynamically added cards
   */
  function setupMutationObserver() {
    var observer = new MutationObserver(function(mutations) {
      var hasNewCards = mutations.some(function(mutation) {
        return Array.from(mutation.addedNodes).some(function(node) {
          if (node.nodeType !== 1) return false;
          // Check if the added node is a card-product or contains one
          return node.tagName === 'CARD-PRODUCT' ||
                 (node.querySelector && node.querySelector('card-product'));
        });
      });

      if (hasNewCards) {
        // Debounce to let DOM settle after innerHTML replacements
        clearTimeout(mutationDebounceTimer);
        mutationDebounceTimer = setTimeout(function() {
          processAllCards(false);
        }, 100);
      }
    });

    // Start observing
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      });
    }
  }

  // Read-only interface for Recently Viewed (CU-86d46vcvq). Assigned when this
  // deferred script runs, before init, so every later deferred script can use it.
  window.CardVariantFilter = {
    getDeliveryPreference: getDeliveryPreference,
    summariseAvailability: summariseAvailability,
    isPurchasable: function(availabilityData, deliveryPreference) {
      return summariseAvailability(availabilityData, deliveryPreference).availableIds.length > 0;
    }
  };

  /**
   * Initialize the card variant filter
   */
  function init() {
    processAllCards(false);
    setupEventListeners();
    setupMutationObserver();
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
